import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';
import { Role } from 'stores/AuthenticationStoreAdapter';

// --- 型定義 ---

export type CreateUserParams = {
  userId: string;
  mailAddress: string;
  userName: string;
  password: string;
};

export type UpdateUserParams = {
  userId: string;
  mailAddress?: string;
  userName?: string;
  password?: string;
};

export type DeleteUserParams = {
  userId: string;
};

export type GetUserParams = {
  userId?: string;
  mailAddress?: string;
  userName?: string;
};

export type UserResult = {
  userId: string;
  mailAddress: string;
  userName: string;
  roles: Role[];
};

export type CreateUserRoleParams = {
  userId: string;
  roleIds: Role[];
};

// --- API関数 ---

/**
 * ユーザー登録API
 * @param params.userId - ユーザーID（必須, 最大300文字）
 * @param params.mailAddress - メールアドレス（必須, 最大300文字）
 * @param params.userName - ユーザー名（必須, 最大300文字）
 * @param params.password - パスワード（必須, 最大300文字）
 */
export const createUser = async (params: CreateUserParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.CREATE_USER, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ユーザー更新API
 * @param params.userId - ユーザーID（必須, 最大300文字）
 * @param params.mailAddress - メールアドレス（任意, 最小1文字, 最大300文字）
 * @param params.userName - ユーザー名（任意, 最小1文字, 最大300文字）
 * @param params.password - パスワード（任意, 最小1文字, 最大300文字）
 */
export const updateUser = async (params: UpdateUserParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.UPDATE_USER, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ユーザー削除API
 * @param params.userId - ユーザーID（必須, 最大300文字）
 */
export const deleteUser = async (params: DeleteUserParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.DELETE_USER, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ユーザー取得API
 * @param params.userId - ユーザーID（任意, 最大300文字, 前方一致）
 * @param params.mailAddress - メールアドレス（任意, 最大300文字, 前方一致）
 * @param params.userName - ユーザー名（任意, 最大300文字, 前方一致）
 */
export const getUser = async (params: GetUserParams): Promise<UserResult[]> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.GET_USER, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ユーザー権限登録API
 * @param params.userId - ユーザーID（必須, 最大300文字）
 * @param params.roleIds - 権限リスト（必須, 最小1件, 値: "ADMIN" | "GENERAL" | "MANAGER" | "BACK_OFFICE" | "DEVELOPER"）
 */
export const createUserRole = async (params: CreateUserRoleParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.CREATE_USER_ROLE, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
